import {type Insets, Platform} from 'react-native'
import {type Service} from '@atproto/lex'
import {api} from '@bsky/sdk'

import {
  APPVIEW_PROXY_DID,
  APPVIEW_PROXY_SERVICE,
  CHAT_PROXY_DID,
  IS_DEV,
  PUBLIC_APPVIEW_URL,
} from '#/env'
import {type app} from '#/lexicons'

export const LOCAL_DEV_SERVICE =
  Platform.OS === 'android' ? 'http://10.0.2.2:2583' : 'http://localhost:2583'
export const STAGING_SERVICE = 'https://staging.bsky.dev'
export const BSKY_SERVICE = 'https://bsky.social'
export const BSKY_SERVICE_DID = 'did:web:bsky.social'
export const EDRIFFLES_ACCOUNT_SERVICE = 'https://plumblines.uk'
/**
 * Optional account entryway used before login and for handle availability.
 * This is not an AppView read authority; public reads use PUBLIC_APPVIEW_URL.
 */
export const PUBLIC_ACCOUNT_SERVICE =
  process.env.EXPO_PUBLIC_ACCOUNT_SERVICE ||
  (process.env.EXPO_PUBLIC_ENV === 'production'
    ? EDRIFFLES_ACCOUNT_SERVICE
    : BSKY_SERVICE)
/** @deprecated Use PUBLIC_APPVIEW_URL. */
export const APPVIEW_ENDPOINT = PUBLIC_APPVIEW_URL
/**
 * Login and account-entryway screens use the explicitly configured account
 * service. Public reads remain scoped to PUBLIC_APPVIEW_URL.
 */
export const DEFAULT_SERVICE = PUBLIC_ACCOUNT_SERVICE
const HELP_DESK_LANG = 'en-us'
export const HELP_DESK_URL = `https://blueskyweb.zendesk.com/hc/${HELP_DESK_LANG}`
export const CHAT_SERVICE = 'https://api.bsky.chat'
export const EMBED_SERVICE = 'https://embed.bsky.app'
export const EMBED_SCRIPT = `${EMBED_SERVICE}/static/embed.js`
export const BSKY_DOWNLOAD_URL = 'https://bsky.app/download'
export const STARTER_PACK_MAX_SIZE = 150
export const CARD_ASPECT_RATIO = 1200 / 630

// HACK
// Yes, this is exactly what it looks like. It's a hard-coded constant
// reflecting the number of new users in the last week. We don't have
// time to add a route to the servers for this so we're just going to hard
// code and update this number with each release until we can get the
// server route done.
// -prf
export const JOINED_THIS_WEEK = 560000 // estimate as of 12/18/24

export const DISCOVER_DEBUG_DIDS: Record<string, true> = {
  'did:plc:oisofpd7lj26yvgiivf3lxsi': true, // hailey.at
  'did:plc:p2cp5gopk7mgjegy6wadk3ep': true, // samuel.bsky.team
  'did:plc:ragtjsm2j2vknwkz3zp4oxrd': true, // pfrazee.com
  'did:plc:vpkhqolt662uhesyj6nxm7ys': true, // why.bsky.team
  'did:plc:3jpt2mvvsumj2r7eqk4gzzjz': true, // esb.lol
  'did:plc:vjug55kidv6sye7ykr5faxxn': true, // emilyliu.me
  'did:plc:tgqseeot47ymot4zro244fj3': true, // iwsmith.bsky.social
  'did:plc:2dzyut5lxna5ljiaasgeuffz': true, // darrin.bsky.team
}

const BASE_FEEDBACK_FORM_URL = `${HELP_DESK_URL}/requests/new`
export function FEEDBACK_FORM_URL({
  email,
  handle,
}: {
  email?: string
  handle?: string
}): string {
  let str = BASE_FEEDBACK_FORM_URL
  if (email) {
    str += `?tf_anonymous_requester_email=${encodeURIComponent(email)}`
    if (handle) {
      str += `&tf_17205412673421=${encodeURIComponent(handle)}`
    }
  }
  return str
}

export const MAX_DISPLAY_NAME = 64
export const MAX_DESCRIPTION = 256

export const MAX_GRAPHEME_LENGTH = 300

export const MAX_DRAFT_GRAPHEME_LENGTH = 1000

export const MAX_DM_GRAPHEME_LENGTH = 1000

export const MAX_GROUP_NAME_GRAPHEME_LENGTH = 50

// Recommended is 100 per: https://www.w3.org/WAI/GL/WCAG20/tests/test3.html
// but increasing limit per user feedback
export const MAX_ALT_TEXT = 2000

export const MAX_REPORT_REASON_GRAPHEME_LENGTH = 2000

export function IS_TEST_USER(handle?: string) {
  return handle && handle?.endsWith('.test')
}

export function IS_PROD_SERVICE(url?: string) {
  return url && url !== STAGING_SERVICE && !url.startsWith(LOCAL_DEV_SERVICE)
}

/**
 * The default Discover feed is deployment configuration, not a Bluesky
 * product constant. An unset owner deliberately points at an unconfigured
 * DID so a deployment fails explicitly instead of silently importing a
 * different operator's algorithm.
 */
export const PROJECT_DEFAULT_FEED_OWNER_DID =
  process.env.EXPO_PUBLIC_DEFAULT_FEED_OWNER_DID ||
  'did:example:unconfigured-feed'
export const PROJECT_DEFAULT_FEED_RKEY =
  process.env.EXPO_PUBLIC_DEFAULT_FEED_RKEY || 'social-discover'

export const PROD_DEFAULT_FEED = (rkey: string) =>
  `at://${PROJECT_DEFAULT_FEED_OWNER_DID}/app.bsky.feed.generator/${
    rkey === 'whats-hot' ? PROJECT_DEFAULT_FEED_RKEY : rkey
  }`

export const STAGING_DEFAULT_FEED = (rkey: string) =>
  `at://${PROJECT_DEFAULT_FEED_OWNER_DID}/app.bsky.feed.generator/${rkey}`

export const PROD_FEEDS = [
  `feedgen|${PROD_DEFAULT_FEED('whats-hot')}`,
  `feedgen|${PROD_DEFAULT_FEED('thevids')}`,
]

export const STAGING_FEEDS = [
  `feedgen|${STAGING_DEFAULT_FEED('whats-hot')}`,
  `feedgen|${STAGING_DEFAULT_FEED('thevids')}`,
]

export const IMAGE_SIZE_CONFIG_POSTS = {
  maxDimension: 4000,
  maxSize: 2000000,
}

export const IMAGE_SIZE_CONFIG_2K_1MB = {
  maxDimension: 2000,
  maxSize: 1000000,
}

export const STAGING_LINK_META_PROXY =
  'https://cardyb.staging.bsky.dev/v1/extract?url='

export const PROD_LINK_META_PROXY = 'https://cardyb.bsky.app/v1/extract?url='

export function LINK_META_PROXY(_serviceUrl: string) {
  if (IS_DEV) {
    return STAGING_LINK_META_PROXY
  }

  return PROD_LINK_META_PROXY
}

export const STATUS_PAGE_URL = 'https://status.bsky.app/'

// Hitslop constants
export const createHitslop = (size: number): Insets => ({
  top: size,
  left: size,
  bottom: size,
  right: size,
})
export const HITSLOP_10 = createHitslop(10)
export const HITSLOP_20 = createHitslop(20)
export const HITSLOP_30 = createHitslop(30)
export const LANG_DROPDOWN_HITSLOP = {top: 10, bottom: 10, left: 4, right: 4}
export const BACK_HITSLOP = HITSLOP_30
export const MAX_POST_LINES = 25

export const BSKY_APP_ACCOUNT_DID = PROJECT_DEFAULT_FEED_OWNER_DID

export const BSKY_FEED_OWNER_DIDS: string[] = []

export const TRENDING_DID =
  process.env.EXPO_PUBLIC_TRENDING_DID || 'did:example:unconfigured-trending'
export const TRENDING_HANDLE =
  process.env.EXPO_PUBLIC_TRENDING_HANDLE || 'trending.example.invalid'

/**
 * The pinned Discover feed belongs to this deployment. Keeping this derived
 * from the configured project feed prevents new accounts and replacement
 * flows from silently reintroducing Bluesky's operator-owned feed.
 */
export const DISCOVER_FEED_URI = PROD_DEFAULT_FEED('whats-hot')
export const VIDEO_FEED_URI =
  'at://did:plc:z72i7hdynmk6r22z27h6tvur/app.bsky.feed.generator/thevids'
export const STAGING_VIDEO_FEED_URI =
  'at://did:plc:yofh3kx63drvfljkibw5zuxo/app.bsky.feed.generator/thevids'
export const VIDEO_FEED_URIS = [VIDEO_FEED_URI, STAGING_VIDEO_FEED_URI]
export const DISCOVER_SAVED_FEED = {
  type: 'feed',
  value: DISCOVER_FEED_URI,
  pinned: true,
}
export const TIMELINE_SAVED_FEED = {
  type: 'timeline',
  value: 'following',
  pinned: true,
}
export const VIDEO_SAVED_FEED = {
  type: 'feed',
  value: VIDEO_FEED_URI,
  pinned: true,
}

export const RECOMMENDED_SAVED_FEEDS: Pick<
  app.bsky.actor.defs.SavedFeed,
  'type' | 'value' | 'pinned'
>[] = [DISCOVER_SAVED_FEED, TIMELINE_SAVED_FEED]

export const KNOWN_SHUTDOWN_FEEDS = [
  'at://did:plc:wqowuobffl66jv3kpsvo7ak4/app.bsky.feed.generator/the-algorithm', // for you by skygaze
]

export const GIF_SERVICE = 'https://gifs.bsky.app'

export const GIF_KLIPY_SEARCH = (params: string) =>
  `${GIF_SERVICE}/klipy/v2/search?${params}`
export const GIF_KLIPY_FEATURED = (params: string) =>
  `${GIF_SERVICE}/klipy/v2/featured?${params}`

export const MAX_LABELERS = 20

export const VIDEO_SERVICE = 'https://video.bsky.app'
export const VIDEO_SERVICE_DID = 'did:web:video.bsky.app'

export const VIDEO_MAX_DURATION_MS = 10 * 60 * 1000 // 10 minutes in milliseconds
/**
 * Maximum size of a video in megabytes, _not_ mebibytes. Backend uses
 * ISO megabytes.
 */
export const VIDEO_MAX_SIZE_MB = 300
export const VIDEO_MAX_SIZE = VIDEO_MAX_SIZE_MB * 1000 * 1000 // 300mb

export const SUPPORTED_MIME_TYPES = [
  'video/mp4',
  'video/mpeg',
  'video/webm',
  'video/quicktime',
  'image/gif',
] as const

export type SupportedMimeTypes = (typeof SUPPORTED_MIME_TYPES)[number]

export const EMOJI_REACTION_LIMIT = 5

export const urls = {
  website: {
    blog: {
      findFriendsAnnouncement:
        'https://bsky.social/about/blog/12-16-2025-find-friends',
      initialVerificationAnnouncement: `https://bsky.social/about/blog/04-21-2025-verification`,
      searchTipsAndTricks: 'https://bsky.social/about/blog/05-31-2024-search',
    },
    support: {
      findFriendsPrivacyPolicy:
        'https://bsky.social/about/support/find-friends-privacy-policy',
    },
  },
}

export const PUBLIC_APPVIEW = PUBLIC_APPVIEW_URL
export const PUBLIC_APPVIEW_DID = APPVIEW_PROXY_DID
export const PUBLIC_STAGING_APPVIEW_DID = APPVIEW_PROXY_DID

// temp hack for e2e - esb
export const BLUESKY_PROXY_HEADER = {
  /** @deprecated Use APPVIEW_PROXY_SERVICE. */
  value: APPVIEW_PROXY_SERVICE,
  get() {
    return this.value
  },
  set(value: string) {
    this.value = value as Service
  },
}

/**
 * The chat service's proxy target, in the `did#service_id` form a lex client's
 * `service` option takes. A client constructed with it emits `atproto-proxy:
 * <this value>` on every request, which is what routes `chat.bsky.*` calls to
 * the chat service.
 *
 * The DID comes from the env-configurable `CHAT_PROXY_DID` (via
 * `EXPO_PUBLIC_CHAT_PROXY_DID`) rather than a hard-coded constant, so the
 * target can be retargeted per environment.
 */
export const CHAT_PROXY_SERVICE: Service = `${CHAT_PROXY_DID}#bsky_chat`

/**
 * Bluesky's own moderation service, in the `did#service_id` form a lex client's
 * per-call `service` option takes. Passing it emits `atproto-proxy: <this
 * value>` on that one request, routing a `com.atproto.moderation.*` call to
 * Bluesky's labeler.
 *
 * Reports and appeals aimed at a DIFFERENT labeler build their own value from
 * that labeler's creator did instead, so this is a per-call option rather than a
 * client-level one like {@link CHAT_PROXY_SERVICE}.
 */
export const MOD_PROXY_SERVICE: Service = `${api.moderation.did}#atproto_labeler`

/**
 * The notification service's proxy target, in the `did#service_id` form a lex
 * client's per-call `service` option takes. Passing it emits `atproto-proxy:
 * <this value>` on that one request, which is what routes push registration to
 * the notification service (replaces the old
 * `BLUESKY_NOTIF_SERVICE_HEADERS`).
 */
export const NOTIF_SERVICE: Service = `${APPVIEW_PROXY_DID}#bsky_notif`

export const webLinks = {
  tos: `https://bsky.social/about/support/tos`,
  privacy: `https://bsky.social/about/support/privacy-policy`,
  community: `https://bsky.social/about/support/community-guidelines`,
  communityDeprecated: `https://bsky.social/about/support/community-guidelines-deprecated`,
}
